import './App.css';
import { lazy, Suspense, useState } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import Navbar from './components/navbar/Navbar';
import Preloader from './components/shared/preloader/Preloader';
import SchoolshowcaseSection from './pages/Academics/SchoolShowcaseSection';
import ProgramDetailsPage from './pages/Academics/formations/details-formation/ProgramDetailsPage';
import Footer from './components/footer/Footer';
import ContactPage from './pages/contact/ContactPage';
import FloatingWhatsApp from './components/shared/floating-icons/FloatingWhatsApp';
import ScrollToTopOffset from './components/shared/scroll-to-top/ScrollToTopOffset';
import PreInscriptionModal from './components/shared/pre-inscription-modal/PreInscriptionModal';
import CalendrierPage from './pages/Academics/formations/formation-a-la-carte/Calendrier';
import SEO from './components/seo/SEO';
import SEOHandler from './components/seo/SEOHandler';

const LandingPage = lazy(() => import('./components/Landing-page'));
const AboutUsPage = lazy(() => import('./pages/about-page/AboutUsPage'));
const SingleEventPage = lazy(
	() => import('./pages/events/single-event/Single-event-page'),
);

const ItSalesPage = lazy(
	() => import('./pages/Academics/formations/it-sales/ItSalesPage'),
);
const AgenticIaPage = lazy(
	() => import('./pages/Academics/formations/agentic-ia/AgenticIaPage'),
);
const FormationALaCartePage = lazy(
	() =>
		import('./pages/Academics/formations/formation-a-la-carte/FormationALaCartePage'),
);

function RouteLoader() {
	return <div className='route-loader'>Chargement...</div>;
}

function PageWithSEO({ seo, children }) {
	return (
		<>
			<SEO {...seo} />
			{children}
		</>
	);
}

function AppRoutes({ preloaderDone, setPreloaderDone }) {
	const location = useLocation();
	const backgroundLocation = location.state?.backgroundLocation;

	return (
		<>
			<ScrollToTopOffset offsetPercent={10} behavior='auto' />

			{!preloaderDone && <Preloader onFinish={() => setPreloaderDone(true)} />}

			<Navbar />
			<SEOHandler />
			<Suspense fallback={<RouteLoader />}>
				<Routes location={backgroundLocation || location}>
					<Route
						path='/'
						element={
							<PageWithSEO
								seo={{
									title: 'Clevory Academy',
									description:
										'Clevory Academy propose des formations professionnelles en intelligence artificielle, IT Sales, digital et compétences métiers.',
									path: '/',
								}}>
								<LandingPage />
							</PageWithSEO>
						}
					/>

					<Route
						path='/qui-sommes-nous'
						element={
							<PageWithSEO
								seo={{
									title: 'À propos',
									description:
										'Découvrez Clevory Academy, notre mission, notre approche pédagogique et notre engagement pour la formation professionnelle.',
									path: '/qui-sommes-nous',
								}}>
								<AboutUsPage />
							</PageWithSEO>
						}
					/>

					<Route
						path='/single-event'
						element={
							<PageWithSEO
								seo={{
									title: 'Événement',
									description:
										'Découvrez les événements et sessions organisés par Clevory Academy.',
									path: '/single-event',
								}}>
								<SingleEventPage />
							</PageWithSEO>
						}
					/>

					<Route path='/formations'>
						<Route
							index
							element={
								<PageWithSEO
									seo={{
										title: 'Nos formations',
										description:
											'Découvrez les formations Clevory Academy en Agentic IA, IT Sales et parcours professionnels adaptés au marché.',
										path: '/formations',
									}}>
									<SchoolshowcaseSection />
								</PageWithSEO>
							}
						/>

						<Route
							path='it-sales'
							element={
								<PageWithSEO
									seo={{
										title: 'Formation IT Sales',
										description:
											'Développez vos compétences en vente IT, prospection, argumentaire commercial et vente de solutions technologiques.',
										path: '/formations/it-sales',
									}}>
									<ItSalesPage />
								</PageWithSEO>
							}
						/>

						<Route
							path='it-sales/program'
							element={
								<PageWithSEO
									seo={{
										title: 'Programme IT Sales',
										description:
											'Consultez le programme détaillé de la formation IT Sales proposée par Clevory Academy.',
										path: '/formations/it-sales/program',
									}}>
									<ProgramDetailsPage />
								</PageWithSEO>
							}
						/>

						<Route
							path='it-sales/pre-inscription'
							element={
								backgroundLocation ? <ItSalesPage /> : <PreInscriptionModal />
							}
						/>

						<Route
							path='agentic-ia'
							element={
								<PageWithSEO
									seo={{
										title: 'Formation Agentic IA',
										description:
											'Apprenez les fondamentaux de l’intelligence artificielle agentique, des agents IA, de l’automatisation et des outils IA modernes.',
										path: '/formations/agentic-ia',
									}}>
									<AgenticIaPage />
								</PageWithSEO>
							}
						/>

						<Route
							path='agentic-ia/program'
							element={
								<PageWithSEO
									seo={{
										title: 'Programme Agentic IA',
										description:
											'Découvrez le programme détaillé de la formation Agentic IA : IA générative, agents IA, Python, LangChain et projets professionnels.',
										path: '/formations/agentic-ia/program',
									}}>
									<ProgramDetailsPage />
								</PageWithSEO>
							}
						/>

						<Route
							path='agentic-ia/pre-inscription'
							element={
								backgroundLocation ? <AgenticIaPage /> : <PreInscriptionModal />
							}
						/>

						<Route
							path='formation-a-la-carte'
							element={
								<PageWithSEO
									seo={{
										title: 'Formation à la carte',
										description:
											'Explorez les formations à la carte de Clevory Academy et choisissez les parcours adaptés à vos besoins professionnels.',
										path: '/formations/formation-a-la-carte',
									}}>
									<FormationALaCartePage />
								</PageWithSEO>
							}
						/>
					</Route>

					<Route
						path='/calendrier'
						element={
							<PageWithSEO
								seo={{
									title: 'Calendrier des formations',
									description:
										'Consultez le calendrier des formations Clevory Academy, les dates de sessions et les événements à venir.',
									path: '/calendrier',
								}}>
								<CalendrierPage />
							</PageWithSEO>
						}
					/>

					<Route
						path='/contact'
						element={
							<PageWithSEO
								seo={{
									title: 'Contact',
									description:
										'Contactez Clevory Academy pour obtenir plus d’informations sur nos formations, programmes et inscriptions.',
									path: '/contact',
								}}>
								<ContactPage />
							</PageWithSEO>
						}
					/>
				</Routes>

				{backgroundLocation && (
					<Routes>
						<Route
							path='/formations/it-sales/pre-inscription'
							element={<PreInscriptionModal />}
						/>
						<Route
							path='/formations/agentic-ia/pre-inscription'
							element={<PreInscriptionModal />}
						/>
					</Routes>
				)}
			</Suspense>

			<FloatingWhatsApp
				phoneNumber='21629376774
'
				message='Bonjour, je souhaite avoir plus d’informations.'
				tooltip='Discuter sur WhatsApp'
				showAfter={150}
			/>

			<Footer />
		</>
	);
}

function App() {
	const [preloaderDone, setPreloaderDone] = useState(false);

	return (
		<HelmetProvider>
			<BrowserRouter>
				<AppRoutes
					preloaderDone={preloaderDone}
					setPreloaderDone={setPreloaderDone}
				/>
			</BrowserRouter>
		</HelmetProvider>
	);
}

export default App;
